import useSWR, { preload } from 'swr';
import { backendClient } from '@/lib/api-client';

// ---------------------------------------------------------------------------
// Two endpoint families back this file:
//   GET /matters/{matterGuid}/chronology-events
//   GET /matters/{matterGuid}/chronologies/{chronologyId}/events
//     light lists — scalars + filter facets
//   GET /matters/{matterGuid}/chronology-events/{eventId}
//     detail — claims + entities + docs
//
// The chronology page renders the light list (cheap, virtualisable). The side
// panel reads detail on row click; SWR caches per-event so re-selection is
// free. ChronologyEventDetail extends ChronologyEventLight so the row card +
// the side panel can share scalar fields without re-mapping.
// ---------------------------------------------------------------------------

export interface ChronologyEntity {
  name: string;
  role: string | null;
  description: string | null;
  entity_type: string | null;
}

export interface ChronologyDocument {
  document_id: number;
  guid: string | null;
  filename: string | null;
  doc_type: string | null;
  doc_type_label: string | null;
}

export interface ChronologyClaimDate {
  start_date: string | null;
  end_date: string | null;
}

export interface ChronologyClaimEntity {
  name: string;
  entity_type: string;
}

export interface ChronologyClaimDocument {
  document_id: number;
  guid: string | null;
  filename: string | null;
  doc_type: string | null;
  doc_type_label: string | null;
  doc_date: string | null;
}

export interface ChronologyClaim {
  assertion_kind: string;
  description: string;
  claim_date: string | null;
  dates: ChronologyClaimDate[];
  asserting_entities: ChronologyClaimEntity[];
  source_documents: ChronologyClaimDocument[];
  side: string | null;
}

export interface ChronologyEventTag {
  id: string;
  tag_key: string;
  label: string;
  description: string | null;
  group_id: string | null;
  group_key: string | null;
  group_label: string | null;
  group_color: string | null;
  sort_order: number | null;
  added_by?: 'user' | 'system' | null;
}

export interface ChronologyEventLight {
  id: string;
  chronology_id: string | null;
  tags: ChronologyEventTag[];
  chronology_type: string | null;
  chronology_name: string | null;
  description: string;
  detail: string | null;
  temporal_expression: string | null;
  temporal_lower: Date | null;
  temporal_upper: Date | null;
  temporal_lower_inf: boolean;
  temporal_upper_inf: boolean;
  temporal_display: string | null;
  start_precision: 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second' | null;
  end_precision: 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second' | null;
  status: string;
  source: 'generated' | 'manual';
  is_locked: boolean;
  is_doc_event: boolean;
  relevance_score: number | null;
  created_by_label: string | null;
  // Filter facets carried with the list payload for client-side filtering.
  entity_names: string[];
  source_party_names: string[];
  source_document_names: string[];
  source_document_dates: Date[];
}

/** A reader can pass over an entry the build ranked as background. */
export function isBackgroundEvent(ev: Pick<ChronologyEventLight, 'relevance_score'>): boolean {
  return ev.relevance_score === 0;
}

export interface ChronologyEventDetail extends ChronologyEventLight {
  entities: ChronologyEntity[];
  direct_entities: ChronologyEntity[];
  documents: ChronologyDocument[];
  direct_source_documents: ChronologyClaimDocument[];
  claims: ChronologyClaim[];
}

// Back-compat alias — existing code that imported `ChronologyEvent` against
// the old fat shape now lands on the detail shape (which is what those call
// sites actually need: claims + entities + documents).
export type ChronologyEvent = ChronologyEventDetail;

interface ListResponse {
  events: ChronologyEventLight[];
}

interface DetailResponse {
  event: ChronologyEventDetail;
}

interface ExportResponse {
  events: ChronologyEventDetail[];
}

type RawChronologyEvent = Omit<ChronologyEventLight, 'temporal_lower' | 'temporal_upper' | 'source_document_dates'> & {
  temporal_lower?: string | null;
  temporal_upper?: string | null;
  source_document_dates?: string[] | null;
  entities?: ChronologyEntity[];
  direct_entities?: ChronologyEntity[];
  documents?: ChronologyDocument[];
  direct_source_documents?: ChronologyClaimDocument[];
  claims?: ChronologyClaim[];
  assertions?: ChronologyClaim[];
};

function documentFilterName(doc: ChronologyDocument): string | null {
  return (
    doc.filename
    || doc.doc_type_label
    || doc.doc_type
    || (doc.document_id ? `Document ${doc.document_id}` : null)
  );
}

function hydrateLight(ev: RawChronologyEvent): ChronologyEventLight {
  const sourceDocumentNames = ev.source_document_names?.length
    ? ev.source_document_names
    : Array.from(new Set(
      (ev.documents || [])
        .map(documentFilterName)
        .filter((name): name is string => !!name),
    )).sort();
  const sourceDocumentDates = (ev.source_document_dates || [])
    .map(date => new Date(date))
    .filter(date => !Number.isNaN(date.getTime()));

  return {
    ...ev,
    temporal_lower: ev.temporal_lower ? new Date(ev.temporal_lower) : null,
    temporal_upper: ev.temporal_upper ? new Date(ev.temporal_upper) : null,
    temporal_lower_inf: !!ev.temporal_lower_inf,
    temporal_upper_inf: !!ev.temporal_upper_inf,
    tags: ev.tags || [],
    created_by_label: ev.created_by_label ?? (ev.source === 'generated' ? 'Crimson' : null),
    entity_names: ev.entity_names || [],
    source_party_names: ev.source_party_names || [],
    source_document_names: sourceDocumentNames,
    source_document_dates: sourceDocumentDates,
  };
}

function hydrateDetail(ev: RawChronologyEvent): ChronologyEventDetail {
  return {
    ...hydrateLight(ev),
    entities: ev.entities || [],
    direct_entities: ev.direct_entities || [],
    documents: ev.documents || [],
    direct_source_documents: ev.direct_source_documents || [],
    claims: ev.claims || ev.assertions || [],
  };
}

const listKey = (matterGuid: string, chronologyId?: string) =>
  chronologyId
    ? `/matters/${matterGuid}/chronologies/${chronologyId}/events`
    : `/matters/${matterGuid}/chronology-events`;

const detailKey = (matterGuid: string, eventId: string, chronologyId?: string | null) =>
  chronologyId
    ? `/matters/${matterGuid}/chronology-events/${eventId}?chronology_id=${encodeURIComponent(chronologyId)}`
    : `/matters/${matterGuid}/chronology-events/${eventId}`;

const listFetcher = async (url: string): Promise<ListResponse> => {
  const r = await backendClient.get(url);
  return {
    events: (r.data.events || []).map(hydrateLight),
  };
};

const detailFetcher = async (url: string): Promise<DetailResponse> => {
  const r = await backendClient.get(url);
  return { event: hydrateDetail(r.data.event) };
};

export async function fetchChronologyExport(
  matterGuid: string,
  chronologyId?: string,
): Promise<ExportResponse> {
  const url = chronologyId
    ? `/matters/${matterGuid}/chronologies/${chronologyId}/events/export`
    : `/matters/${matterGuid}/chronology-events/export`;
  const r = await backendClient.get(url);
  return {
    events: (r.data.events || []).map(hydrateDetail),
  };
}

export function useChronology(matterGuid: string | undefined, chronologyId?: string | null) {
  const { data, error, mutate, isLoading } = useSWR<ListResponse>(
    matterGuid ? listKey(matterGuid, chronologyId ?? undefined) : null,
    listFetcher,
    { revalidateOnFocus: false, dedupingInterval: 5000 },
  );
  return {
    events: data?.events || [],
    isLoading,
    isError: error,
    mutate,
  };
}

export function useEventDetail(
  matterGuid: string | undefined,
  eventId: string | null | undefined,
  chronologyId?: string | null,
) {
  const { data, error, isLoading, mutate } = useSWR<DetailResponse>(
    matterGuid && eventId ? detailKey(matterGuid, eventId, chronologyId) : null,
    detailFetcher,
    { revalidateOnFocus: false, dedupingInterval: 5000 },
  );
  return {
    event: data?.event,
    isLoading,
    isError: error,
    mutate,
  };
}

// Hover-prefetch: warm the SWR cache before the user clicks. ``preload`` is
// a no-op when the key is already cached, so hovering 100 rows costs at most
// 100 detail fetches (and only if no detail was loaded yet).
export function prefetchEventDetail(
  matterGuid: string | undefined,
  eventId: string,
  chronologyId?: string | null,
) {
  if (!matterGuid) return;
  preload(detailKey(matterGuid, eventId, chronologyId), detailFetcher);
}
